import { OEP_FILE_CATEGORY, type OEPFileKind } from 'agu_oep_core';
import { PersonRows, type OEPActivityPerson, type OEPActivityPersonVM, type OEPActivityRole } from '../activity/activity.vm';
import { Str, StrOrNull, Up, type OEPRow } from '../shared/rows';

export const OEP_BAD_EMAIL = 'Enter one email address.';
export const OEP_NO_PRESENTER = 'No OEP presenter for this season has that email.';
export const OEP_EMPTY_TITLE = 'Enter a presentation name.';
export const OEP_NOT_UPLOADED = 'Not uploaded';
export const OEP_UM_PAGE_SIZE = 25;

const NOUN: Readonly<Record<OEPActivityRole, [string, string]>> = {
    Presenter: ['presenter', 'presenters'],
    Reviewer: ['reviewer', 'reviewers'],
    Nominator: ['nominator', 'nominators'],
    Committee: ['section committee member', 'section committee members'],
};

export interface OEPSectionOption {
    readonly ID: string;
    readonly Name: string;
    readonly Code: string;
    readonly Label: string;
}

export interface OEPUploadedFileVM {
    readonly FileID: string;
    readonly Name: string;
    readonly ProviderKey: string;
}

export interface OEPUserRecordVM {
    readonly UserID: string;
    readonly Name: string;
    readonly Email: string;
    readonly PresentationID: string;
    readonly SectionID: string;
    readonly Title: string;
    readonly PresentationStatus: string;
    readonly PresenterRoleRowIDs: readonly string[];
    readonly IsActive: boolean;
    readonly DraftFile: OEPUploadedFileVM | null;
    readonly FinalFile: OEPUploadedFileVM | null;
}

export interface OEPUserLookupInput {
    readonly Users: readonly OEPRow[];
    readonly UserRoles: readonly OEPRow[];
    readonly Presentations: readonly OEPRow[];
    readonly Files: readonly OEPRow[];
    readonly Links: readonly OEPRow[];
}

export interface OEPUserDraftInput {
    readonly SectionID: string;
    readonly Title: string;
    readonly IsActive: boolean;
}

export interface OEPPeoplePageVM {
    readonly Rows: OEPActivityPersonVM[];
    readonly Page: number;
    readonly TotalPages: number;
    readonly TotalMatches: number;
    readonly CountLine: string;
}

export type OEPUserSavePlan =
    | { readonly Ok: false; readonly Error: string }
    | {
        readonly Ok: true;
        readonly HasChanges: boolean;
        readonly ConfirmInactivate: boolean;
        readonly UpdatePresentation: { readonly SectionID: string; readonly Title: string; readonly Status: string } | null;
        readonly GrantPresenterRole: boolean;
        readonly RevokePresenterRoleRowIDs: readonly string[];
    };

/** One email address: RFC 5322 atext local part (apostrophe included — it is escaped), dotted domain labels. */
const EMAIL_PATTERN = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

/** Trimmed, lower-cased, and shaped like one email address (<= 254 chars); null otherwise. */
export function NormalizePresenterEmail(raw: string): string | null {
    const e = raw.trim().toLowerCase();
    return e.length > 0 && e.length <= 254 && EMAIL_PATTERN.test(e) ? e : null;
}

/** Exact-match filter on MJ: Users.Email with single quotes doubled; pass only a NormalizePresenterEmail result. */
export function UserEmailFilter(email: string): string {
    return `Email = '${email.replace(/'/g, '\'\'')}'`;
}

/** Formats active section rows for the Section dropdown. */
export function ParseSections(rows: readonly OEPRow[]): OEPSectionOption[] {
    return rows.map((r) => {
        const ID = Str(r, 'ID');
        const Name = Str(r, 'Name');
        const Code = Str(r, 'Code');
        return { ID, Name, Code, Label: Code ? `${Name} (${Code})` : Name };
    });
}

/** Picks the presenter's active-season presentation: prefers a non-Canceled row over Canceled, newest first. */
export function PickPresenterPresentation(rows: readonly OEPRow[]): OEPRow | null {
    if (rows.length === 0) return null;
    const live = rows.filter((r) => Str(r, 'Status') !== 'Canceled');
    const pool = live.length > 0 ? live : rows;
    return pool[pool.length - 1] ?? null;
}

/** Appends inactivated (Status = Canceled) presenters so staff can find, filter, and re-activate them in User management. */
export function MergeCanceledPresenters(
    people: readonly OEPActivityPerson[],
    canceledPresentations: readonly OEPRow[],
    canceledUsers: readonly OEPRow[],
    sections: readonly OEPSectionOption[],
): OEPActivityPerson[] {
    const seenPresenters = new Set(people.filter((p) => p.Role === 'Presenter').map((p) => Up(p.UserID)));
    const userByID = new Map(canceledUsers.map((u) => [Up(Str(u, 'ID')), u]));
    const sectionByID = new Map(sections.map((s) => [Up(s.ID), s.Name]));
    const extra: OEPActivityPerson[] = [];

    for (const pres of canceledPresentations) {
        const uid = Str(pres, 'PresenterUserID');
        const key = Up(uid);
        if (!uid || seenPresenters.has(key)) continue;
        const u = userByID.get(key);
        if (!u) continue;
        seenPresenters.add(key);
        extra.push(canceledPersonRow(uid, u, pres, sectionByID.get(Up(Str(pres, 'SectionID'))) ?? null));
    }
    return [...people, ...extra];
}

/** Formats a user's display name from FirstName + LastName (ignoring '.' placeholders), falling back to Name or Email. */
export function DisplayUserName(r: OEPRow): string {
    const parts = [StrOrNull(r, 'FirstName'), StrOrNull(r, 'LastName')]
        .map((p) => (p || '').trim())
        .filter((p) => p !== '' && p !== '.');
    if (parts.length > 0) return parts.join(' ');
    return (StrOrNull(r, 'Name') || '').trim() || Str(r, 'Email');
}

function canceledPersonRow(userID: string, user: OEPRow, pres: OEPRow, section: string | null): OEPActivityPerson {
    return {
        Role: 'Presenter', UserID: userID, Name: DisplayUserName(user), Email: Str(user, 'Email'),
        Institution: null, CareerStage: null, Section: section,
        PresentationCode: StrOrNull(pres, 'PresentationCode'), PresentationTitle: StrOrNull(pres, 'Title'), PresentationFormat: StrOrNull(pres, 'Format'),
        DraftUploads: 0, DraftFileName: null, DraftFilePath: null, DraftUploadedAt: null,
        FinalUploads: 0, FinalFileName: null, FinalFilePath: null, FinalUploadedAt: null,
        ReviewsAssigned: null, ReviewsSubmitted: null, NominationsSubmitted: null, SlateStatus: null,
        FirstSignInAt: null, LastSignInAt: null, SignInDays: 0, FirstOEPAt: null, LastOEPAt: null, OEPVisits: 0, OEPPageViews: 0, OEPDays: 0,
        Canceled: true,
    };
}

/** Paginates filtered people rows (25 per page by default) and formats the count line. */
export function BuildPeoplePage(
    inRole: readonly OEPActivityPerson[],
    matches: readonly OEPActivityPerson[],
    role: OEPActivityRole,
    requestedPage: number,
    pageSize = OEP_UM_PAGE_SIZE,
): OEPPeoplePageVM {
    const total = matches.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, requestedPage), totalPages);
    const offset = (page - 1) * pageSize;
    const rows = PersonRows(matches.slice(offset, offset + pageSize));
    const [one, many] = NOUN[role];
    const countLine =
        inRole.length === 0 ? `No ${many} in this season yet`
            : total === 0 ? `No ${many} match`
                : total <= pageSize ? `Showing all ${total} ${total === 1 ? one : many}`
                    : `Showing ${offset + 1}–${offset + rows.length} of ${total} ${many}`;
    return { Rows: rows, Page: page, TotalPages: totalPages, TotalMatches: total, CountLine: countLine };
}

function latestFile(files: readonly OEPRow[], linkedIDs: ReadonlySet<string>, kind: OEPFileKind): OEPUploadedFileVM | null {
    const category = OEP_FILE_CATEGORY[kind];
    const row = files.find((f) => Str(f, 'Category') === category && Str(f, 'Status') === 'Uploaded' && linkedIDs.has(Up(Str(f, 'ID'))));
    if (!row) return null;
    return {
        FileID: Str(row, 'ID'),
        Name: Str(row, 'Name'),
        ProviderKey: Str(row, 'ProviderKey'),
    };
}

/** Assembles the record view-model from the batch query results, or null if no presenter presentation matches. */
export function BuildUserManagementRecord(input: OEPUserLookupInput): OEPUserRecordVM | null {
    if (input.Users.length !== 1) return null;
    const user = input.Users[0];
    const pres = PickPresenterPresentation(input.Presentations);
    if (!pres) return null;

    const userID = Str(user, 'ID');
    const presentationID = Str(pres, 'ID');
    const presentationStatus = Str(pres, 'Status');
    const roleRowIDs = input.UserRoles
        .filter((r) => Up(Str(r, 'UserID')) === Up(userID))
        .map((r) => Str(r, 'ID'));
    const linked = new Set(
        input.Links
            .filter((l) => Up(Str(l, 'RecordID')) === Up(presentationID))
            .map((l) => Up(Str(l, 'FileID'))),
    );

    return {
        UserID: userID,
        Name: DisplayUserName(user),
        Email: Str(user, 'Email'),
        PresentationID: presentationID,
        SectionID: Str(pres, 'SectionID'),
        Title: Str(pres, 'Title'),
        PresentationStatus: presentationStatus,
        PresenterRoleRowIDs: roleRowIDs,
        IsActive: roleRowIDs.length > 0 && presentationStatus !== 'Canceled',
        DraftFile: latestFile(input.Files, linked, 'Draft'),
        FinalFile: latestFile(input.Files, linked, 'Final'),
    };
}

/** Validates the draft edits and computes the exact presentation and role mutations needed. */
export function PlanUserManagementSave(record: OEPUserRecordVM, draft: OEPUserDraftInput): OEPUserSavePlan {
    const title = draft.Title.trim();
    if (title.length === 0) return { Ok: false, Error: OEP_EMPTY_TITLE };

    const targetStatus = draft.IsActive
        ? (record.PresentationStatus === 'Canceled' ? 'Draft' : record.PresentationStatus)
        : 'Canceled';

    const presChanged =
        Up(draft.SectionID) !== Up(record.SectionID) ||
        title !== record.Title ||
        targetStatus !== record.PresentationStatus;

    const grantRole = draft.IsActive && record.PresenterRoleRowIDs.length === 0;
    const revokeIDs = !draft.IsActive ? record.PresenterRoleRowIDs : [];
    const hasChanges = presChanged || grantRole || revokeIDs.length > 0;
    const confirmInactivate = !draft.IsActive && (record.IsActive || revokeIDs.length > 0 || record.PresentationStatus !== 'Canceled');

    return {
        Ok: true,
        HasChanges: hasChanges,
        ConfirmInactivate: confirmInactivate,
        UpdatePresentation: presChanged ? { SectionID: draft.SectionID, Title: title, Status: targetStatus } : null,
        GrantPresenterRole: grantRole,
        RevokePresenterRoleRowIDs: revokeIDs,
    };
}
